import React, {
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
} from '@mui/material';
import { NoteAddOutlined, CancelOutlined } from '@mui/icons-material';
import PropTypes from 'prop-types';
import moment from 'moment';

import {
  LeadCallOutcome,
  LeadFollowUpReason,
  LeadIntentFlag,
  LeadLostReason,
  LeadHistoryType,
  SalesStage,
  isLockedLeadCallOutcome,
} from '../../../../Model/Lead';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useToast from '../../../../hooks/useToast';
import InputLabel from '../../../../../components/v2/InputLabel';
import DialogRoundedModal from '../../../../../components/DialogRoundedModal';
import PrimaryButton from '../../../../../components/Button/ActionButtons/PrimaryButton';
import { StyledTextField } from '../../../../../components/Inputs';
import LoadingOverlay from '../../../LoadingOverlay';
import { rescheduleViaSDR } from '../../../../utils/lead';
import outcomeReasons from '../../outcomeReasons.json';
import { DateFormat } from '../../../../../utils/date';

import {
  StyledSelectField,
  RecordOutcomeSection,
  ModalContent,
  PreviousAttemptsList,
  PreviousAttemptItem,
  OutcomeNoteSection,
  FollowUpDateTimeSection,
  IntentFlagOption,
  IntentFlagDot,
} from './styles';
import texts from './texts';
import { getCallOutcomeValidationError } from './validations';
import {
  CallType,
  CallTypeLabel,
  DefaultReason,
  EMPTY_VALUE,
  MAX_CALL_ATTEMPTS,
  hasSentToRescheduleHistoryRecord,
  getRecentConsecutiveNotAnsweredAttempts,
  getSortedCallOutcomeHistoryRecords,
  isIntentFlagApplicable,
} from './utils';

const selectableLeadOutcomes = Object.entries(LeadCallOutcome)
  .filter(([, outcome]) => !isLockedLeadCallOutcome(outcome));

const leadOutcomeOptions = selectableLeadOutcomes.map(([key, outcome]) => (
  <MenuItem key={key} value={outcome}>{texts.leadCallOutcome[outcome]}</MenuItem>
));

const lostReasonOptions = Object.values(LeadLostReason).map((reason) => (
  <MenuItem key={reason} value={reason}>{outcomeReasons.lost[reason]}</MenuItem>
));

const followUpReasonOptions = Object.values(LeadFollowUpReason).map((reason) => (
  <MenuItem key={reason} value={reason}>{outcomeReasons.followUp[reason]}</MenuItem>
));

const intentFlagOptions = Object.values(LeadIntentFlag).map((flag) => (
  <MenuItem key={flag} value={flag}>
    <IntentFlagOption>
      <IntentFlagDot $flag={flag} />
      {texts.intentFlagOption[flag]}
    </IntentFlagOption>
  </MenuItem>
));

const CallOutcomeModal = ({
  lead,
  onClose,
  showModal,
  userDoc,
  onCloseLeadModal,
  initialOutcome,
  isOutcomeLocked,
  isSDRView,
  onAfterSave,
  history,
  hideCancelButton,
}) => {
  const isLockedOutcome = isOutcomeLocked && isLockedLeadCallOutcome(initialOutcome);
  const [leadCallOutcome, setLeadCallOutcome] = useState(initialOutcome);
  const [callOutcomeNote, setCallOutcomeNote] = useState(EMPTY_VALUE);
  const [attemptType, setAttemptType] = useState(
    initialOutcome === LeadCallOutcome.NOT_ANSWERED ? CallType.RANG : EMPTY_VALUE,
  );
  const [reason, setReason] = useState(DefaultReason[initialOutcome]);
  // Mandatory, no default: pre-filled only when a flag was already captured on an earlier touch.
  const [selectedIntentFlag, setSelectedIntentFlag] = useState(lead.intentFlag || EMPTY_VALUE);
  const [followUpDate, setFollowUpDate] = useState(() => (
    lead.followUpDate
      ? moment.utc(lead.followUpDate.toDate()).local().format(DateFormat.DATETIME_LOCAL_INPUT_FORMAT)
      : moment().format(DateFormat.DATETIME_LOCAL_INPUT_FORMAT)
  ));
  const [isLoading, setIsLoading] = useState(false);
  const [isAutoLostConfirmed, setIsAutoLostConfirmed] = useState(false);
  const minimumFollowUpDate = moment().format(DateFormat.DATETIME_LOCAL_INPUT_FORMAT);

  const isComponentMountedRef = useComponentMounted();

  const outcomeHistoryRecords = useMemo(() => (
    showModal && leadCallOutcome === LeadCallOutcome.NOT_ANSWERED
      ? getSortedCallOutcomeHistoryRecords(history)
      : []
  ), [showModal, leadCallOutcome, history]);

  const pastAttempts = useMemo(
    () => getRecentConsecutiveNotAnsweredAttempts(outcomeHistoryRecords),
    [outcomeHistoryRecords],
  );

  const isMaxAttemptsReached = pastAttempts.length >= MAX_CALL_ATTEMPTS;
  const nextAttemptCount = pastAttempts.length + 1;
  const isScheduledCallFlow = lead.salesStage === SalesStage.CALENDLY_SCHEDULED
    || hasSentToRescheduleHistoryRecord(history);

  const isAutoLostScenario = leadCallOutcome === LeadCallOutcome.NOT_ANSWERED
    && isScheduledCallFlow
    && nextAttemptCount === MAX_CALL_ATTEMPTS;

  const isIntentFlagRequired = isIntentFlagApplicable(leadCallOutcome, reason);

  const { showToast } = useToast();

  const onSaveCallOutcome = useCallback(async () => {
    try {
      const validationError = getCallOutcomeValidationError({
        callOutcomeNote,
        leadCallOutcome,
        reason,
        isMaxAttemptsReached,
        isAutoLostScenario,
        isAutoLostConfirmed,
        attemptType,
        followUpDate,
        isIntentFlagRequired,
        intentFlag: selectedIntentFlag,
      });
      if (validationError) {
        showToast(validationError, { error: true });
        return;
      }

      const formattedFollowUpDate = followUpDate
        ? moment(followUpDate, DateFormat.DATETIME_LOCAL_INPUT_FORMAT).utc().toDate()
        : null;

      let historyValue = leadCallOutcome;
      if (leadCallOutcome === LeadCallOutcome.NOT_ANSWERED) {
        historyValue = { outcome: leadCallOutcome, attemptCount: nextAttemptCount, attemptType };
      } else if (leadCallOutcome === LeadCallOutcome.FOLLOW_UP) {
        historyValue = {
          outcome: leadCallOutcome,
          followUpDate: formattedFollowUpDate,
        };
      }

      setIsLoading(true);
      if (userDoc) {
        if (leadCallOutcome === LeadCallOutcome.NOT_ANSWERED) {
          await lead.addCallAttempt({
            attemptCount: nextAttemptCount,
            attemptType,
          });
        }
        await lead.addItemToHistory({
          value: historyValue,
          createdAt: new Date(),
          activityType: LeadHistoryType.CALL_OUTCOME,
          createdBy: userDoc.id,
          createdByUserName: `${userDoc.firstName} ${userDoc.lastName}`,
          note: callOutcomeNote,
          ...(reason && { reason }),
        });
        if (isIntentFlagRequired && selectedIntentFlag !== lead.intentFlag) {
          await lead.setIntentFlag(selectedIntentFlag, {
            userId: userDoc.id,
            userName: `${userDoc.firstName} ${userDoc.lastName}`,
          });
        }
        switch (leadCallOutcome) {
          case LeadCallOutcome.LOST:
            await lead.setSalesStage(SalesStage.LOST, {
              userId: userDoc.id,
              userName: userDoc.name,
              note: callOutcomeNote,
              ...(reason && { reason }),
            });
            break;
          case LeadCallOutcome.NOT_ANSWERED:
            // Preserve scheduled-call flow even after first attempt moves stage to IN_DIALOGUE.
            if (isScheduledCallFlow) {
              if (nextAttemptCount === MAX_CALL_ATTEMPTS) {
                await lead.addItemToHistory({
                  value: LeadCallOutcome.LOST,
                  createdAt: new Date(),
                  activityType: LeadHistoryType.CALL_OUTCOME,
                  createdBy: 'SYSTEM',
                  createdByUserName: 'SYSTEM',
                  reason: LeadLostReason.NOT_SERIOUS,
                });
                await lead.setSalesStage(SalesStage.LOST, {
                  userId: 'SYSTEM',
                  userName: 'SYSTEM',
                  reason: LeadLostReason.NOT_SERIOUS,
                });
              } else {
                await rescheduleViaSDR(lead, callOutcomeNote || texts.noAnswer, userDoc.id, userDoc.name);
              }
            } else {
              await lead.setSalesStage(SalesStage.NEW_LEAD);
            }
            break;
          case LeadCallOutcome.FOLLOW_UP:
            await lead.setSalesStage(SalesStage.FOLLOW_UP, {
              userId: userDoc.id,
              userName: userDoc.name,
              note: callOutcomeNote,
              reason,
            });
            await lead.setFollowUpDate(formattedFollowUpDate);
            break;
          default:
            break;
        }
        if (!isSDRView) {
          await userDoc.updateIsISOnCall(true);
        }
        if (onAfterSave) {
          await onAfterSave();
        }
        if (isComponentMountedRef.current) {
          showToast(texts.success);
          onCloseLeadModal();
        }
      }
    } catch (error) {
      showToast(texts.somethingWentWrong, { error: true });
    } finally {
      setIsLoading(false);
    }
  }, [
    lead,
    userDoc,
    showToast,
    leadCallOutcome,
    callOutcomeNote,
    reason,
    nextAttemptCount,
    isScheduledCallFlow,
    attemptType,
    isAutoLostScenario,
    isAutoLostConfirmed,
    isMaxAttemptsReached,
    followUpDate,
    isIntentFlagRequired,
    selectedIntentFlag,
    isComponentMountedRef,
    onCloseLeadModal,
    isSDRView,
    onAfterSave,
  ]);

  return (
    <DialogRoundedModal
      title={texts.title}
      fullWidth
      open={showModal}
      onClose={onClose}
      actionButtons={(
        <>
          <PrimaryButton
            icon={<NoteAddOutlined />}
            onClick={onSaveCallOutcome}
          >
            {texts.saveRecord}
          </PrimaryButton>
          {!hideCancelButton && (
            <PrimaryButton
              icon={<CancelOutlined />}
              onClick={onClose}
              variant="light"
            >
              {texts.cancel}
            </PrimaryButton>
          )}
        </>
      )}
    >
      <ModalContent>
        <RecordOutcomeSection>
          <InputLabel>{texts.leadOutcome}</InputLabel>
          <StyledSelectField
            value={leadCallOutcome}
            onChange={(e) => {
              const selectedOutcome = e.target.value;
              setLeadCallOutcome(selectedOutcome);
              setAttemptType(selectedOutcome === LeadCallOutcome.NOT_ANSWERED ? CallType.RANG : EMPTY_VALUE);
              setReason(DefaultReason[selectedOutcome]);
            }}
            disabled={isOutcomeLocked}
          >
            {isLockedOutcome ? (
              <MenuItem value={leadCallOutcome}>
                {texts.leadCallOutcome[leadCallOutcome]}
              </MenuItem>
            ) : (
              leadOutcomeOptions
            )}
          </StyledSelectField>
          {leadCallOutcome === LeadCallOutcome.LOST && (
            <>
              <InputLabel>{texts.lostReasonLabel}</InputLabel>
              <StyledSelectField
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                {lostReasonOptions}
              </StyledSelectField>
            </>
          )}
          {leadCallOutcome === LeadCallOutcome.NOT_ANSWERED && pastAttempts.length > 0 && (
            <>
              <InputLabel>{texts.previousAttemptsLabel}</InputLabel>
              <PreviousAttemptsList>
                {pastAttempts.map((attempt) => (
                  <PreviousAttemptItem key={`${attempt.createdBy}-${attempt.createdAt}`}>
                    {`Attempt ${attempt.attemptCount} · ${CallTypeLabel[attempt.attemptType] || attempt.attemptType}`}
                  </PreviousAttemptItem>
                ))}
              </PreviousAttemptsList>
            </>
          )}
          {leadCallOutcome === LeadCallOutcome.NOT_ANSWERED && !isMaxAttemptsReached && (
            <>
              <InputLabel>{texts.attemptTypeLabel}</InputLabel>
              <RadioGroup
                row
                value={attemptType}
                onChange={(e) => setAttemptType(e.target.value)}
              >
                {Object.values(CallType).map((type) => (
                  <FormControlLabel
                    key={type}
                    value={type}
                    control={<Radio size="small" />}
                    label={texts.attemptTypeOption[type]}
                  />
                ))}
              </RadioGroup>
            </>
          )}
          {isAutoLostScenario && (
            <>
              <Alert severity="warning" sx={{ mt: 1 }}>{texts.autoLostWarning}</Alert>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={isAutoLostConfirmed}
                    onChange={(e) => setIsAutoLostConfirmed(e.target.checked)}
                    size="small"
                  />
                )}
                label={texts.autoLostConfirmLabel}
              />
            </>
          )}
          {leadCallOutcome === LeadCallOutcome.FOLLOW_UP && (
            <>
              <InputLabel>{texts.followUpReasonLabel}</InputLabel>
              <StyledSelectField
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              >
                {followUpReasonOptions}
              </StyledSelectField>
              <FollowUpDateTimeSection>
                <InputLabel>{texts.followUpDateTime}</InputLabel>
                <StyledTextField
                  fullWidth
                  type="datetime-local"
                  value={followUpDate}
                  inputProps={{ min: minimumFollowUpDate }}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </FollowUpDateTimeSection>
            </>
          )}
          {isIntentFlagRequired && (
            <>
              <InputLabel>{texts.intentFlagLabel}</InputLabel>
              <StyledSelectField
                value={selectedIntentFlag}
                onChange={(e) => setSelectedIntentFlag(e.target.value)}
              >
                {intentFlagOptions}
              </StyledSelectField>
            </>
          )}
          <OutcomeNoteSection>
            <InputLabel>{texts.outcomeNote}</InputLabel>
            <StyledTextField
              fullWidth
              multiline
              rows={3}
              value={callOutcomeNote}
              onChange={(e) => setCallOutcomeNote(e.target.value)}
            />
          </OutcomeNoteSection>
        </RecordOutcomeSection>
      </ModalContent>
      <LoadingOverlay isLoading={isLoading} />
    </DialogRoundedModal>
  );
};

CallOutcomeModal.propTypes = {
  lead: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  showModal: PropTypes.bool.isRequired,
  userDoc: PropTypes.object.isRequired,
  onCloseLeadModal: PropTypes.func.isRequired,
  initialOutcome: PropTypes.string,
  isOutcomeLocked: PropTypes.bool,
  isSDRView: PropTypes.bool,
  onAfterSave: PropTypes.func,
  history: PropTypes.arrayOf(PropTypes.object),
  hideCancelButton: PropTypes.bool,
};

CallOutcomeModal.defaultProps = {
  initialOutcome: LeadCallOutcome.FOLLOW_UP,
  isOutcomeLocked: false,
  isSDRView: false,
  onAfterSave: () => {},
  history: null,
  hideCancelButton: false,
};

export default CallOutcomeModal;
